import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { PlanFeaturesApi } from './interfaces/dashboard.component';
import { SavedLayoutItem } from './interfaces/dashboard-layout';

export interface ExamSliceApi {
  label: string;
  short: string;
  value: number;
  percent: number;
}

export interface RecentExamApi {
  AutoIndex: number;
  PID: number;
  ExamType: string;
  CreationTime: string;
  UID: number | null;
  ClosedDate: string | null;
  First: string;
  Last: string;
  OpenerName: string | null;
}

export interface BirthdayApi {
  PID: number;
  First: string;
  Last: string;
  Birth: string;
  Phone?: string;
  Mobile?: string;
  Email?: string;
}

export interface RecentLinkApi {
  First: string;
  Last: string;
  created: string;
  Views: number;
  LinkType: 'consent' | 'exam';
  url: string;
}

export interface StorageApi {
  examCount: number;
  imageCount: number;
  videoCount: number;
}

export interface StorageSizeApi {
  bytes: number;
  formattedSize: string;
}

export interface BillingApi {
  total: number;
  count: number;
  isClinicWide: boolean;
}

export interface DashboardResponse {
  birthdays: BirthdayApi[];
  skipOnLogin: boolean;
  // null → never customized or unreadable blob; client renders its default.
  layout: SavedLayoutItem[] | null;
}

export interface AppointmentApi {
  AutoIndex: number;
  ExamDate: string;
  ExamCode: string;
  First: string;
  Last: string;
}

export interface InvoiceApi {
  AutoIndex: number;
  Price: number;
  DateCreated: string;
  Recipient: string;
  PatientID: string;
  PID: number;
  DocumentNumber: string;
  TypeName: string;
  OriginLink: string;
  CopyLink: string;
}

export interface UpcomingBirthApi {
  First: string;
  Last: string;
  PID: number;
  PregID: number;
  EDD: string;
  Phone?: string;
  Mobile?: string;
  Email?: string;
}

export interface SubscriptionPaymentApi {
  id: string | null;
  amount: number | null;
  date: string | null;
  documentNumber: string | null;
  typeName: string | null;
  copyLink: string | null;
  type?: number | null;
  credit?: boolean; // credit note / refund (vs. a paid invoice)
}

export interface SubscriptionHistoryApi {
  payments: SubscriptionPaymentApi[];
}

export interface DoctorOption {
  uid: number;
  name: string;
}

export interface ExamStatsApi {
  examSlices: ExamSliceApi[];
  examTotal: number;
  examPrevPeriodTotal: number;
  recentExams: RecentExamApi[];
  scope: 'clinic' | 'user';
  canFilterByDoctor: boolean;
  doctors: DoctorOption[];
}

export interface LinkStatsApi {
  linksSent: number;
  linksOpened: number;
  linksUnopened: number;
  recentLinks: RecentLinkApi[];
}

@Injectable({ providedIn: 'root' })
export class DashboardService {
  constructor(private http: HttpClient) {}

  getDashboard(): Observable<DashboardResponse> {
    return this.http.get<DashboardResponse>('/api/dashboard');
  }

  getAppointments(): Observable<AppointmentApi[]> {
    return this.http.get<AppointmentApi[]>('/api/dashboard/appointments');
  }

  getInvoices(): Observable<InvoiceApi[]> {
    return this.http.get<InvoiceApi[]>('/api/dashboard/invoices');
  }

  updatePreferences(skipOnLogin: boolean): Observable<{ status: string }> {
    return this.http.put<{ status: string }>('/api/dashboard/preferences', { skipOnLogin });
  }

  saveDashboardLayout(layout: SavedLayoutItem[]): Observable<{ status: string }> {
    return this.http.put<{ status: string }>('/api/dashboard/layout', { layout });
  }

  getUpcomingBirths(): Observable<UpcomingBirthApi[]> {
    return this.http.get<UpcomingBirthApi[]>('/api/dashboard/patients/upcoming-births');
  }

  getStorageSize(): Observable<StorageSizeApi> {
    return this.http.get<StorageSizeApi>('/api/dashboard/storage');
  }

  getStorageStats(period: 'today' | 'week' | 'month' = 'month'): Observable<StorageApi> {
    return this.http.get<StorageApi>(`/api/dashboard/storage/stats?period=${period}`);
  }

  getExamStats(
    period: 'today' | 'week' | 'month',
    doctorUID?: number | null
  ): Observable<ExamStatsApi> {
    const doctorParam = doctorUID != null ? `&doctorUID=${doctorUID}` : '';
    return this.http.get<ExamStatsApi>(`/api/dashboard/exams?period=${period}${doctorParam}`);
  }

  getLinkStats(period: 'today' | 'week' | 'month'): Observable<LinkStatsApi> {
    return this.http.get<LinkStatsApi>(`/api/dashboard/links?period=${period}`);
  }

  getBillingStats(period: 'today' | 'week' | 'month'): Observable<BillingApi> {
    return this.http.get<BillingApi>(`/api/dashboard/billing?period=${period}`);
  }

  getSubscriptionPayment(): Observable<SubscriptionPaymentApi[] | null> {
    return this.http.get<SubscriptionPaymentApi[] | null>('/api/dashboard/subscription');
  }

  getSubscriptionHistory(): Observable<SubscriptionHistoryApi> {
    return this.http.get<SubscriptionHistoryApi>('/api/dashboard/subscription/history');
  }

  getPlanFeatures(): Observable<PlanFeaturesApi> {
    return this.http.get<PlanFeaturesApi>('/api/dashboard/plan-features');
  }

  requestFeature(featureKey: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>('/api/dashboard/feature-request', { featureKey });
  }
}
